import React, { useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Button,
    Paper,
    useTheme,
    IconButton, TextField
} from '@mui/material';
import { useSensorSetting } from './hooks/useSensorSetting';
import { DOMAINS, useDomain } from '../../../contexts/DomainContext';
import PageHeader from '../../BomManagement/components/PageHeader';
import HelpContent from './components/HelpContent';
import './SensorDeviceManagement.css';
import {EnhancedDataGridWrapper} from "../../Common";
import SaveIcon from "@mui/icons-material/Save";
import {Edit} from "@mui/icons-material";
import useLocalStorageVO from "../../Common/UseLocalStorageVO";

/**
 * 센서 정보수정 컴포넌트
 * 
 * 설비장비와 센서장비의 싱크를 맞추기 위해 설정을 수정하고 관리하는 화면입니다.
 */
const DeviceEquipSync = () => {
  // 전역 상태 및 유틸리티
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // 모달 관련 상태
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  // 설비 목록 관련 상태 관리
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const { loginUser } = useLocalStorageVO();

  const { 
    branchList,
    isLoading,
    selectedBranch,
    handleBranchChange,
    equipmentList,
    sensorDeviceList,
    handleSensorDeviceSave
  } = useSensorSetting();

  // 상세 정보 상태 관리
  const [detailInfo, setDetailInfo] = useState({
      id: null,
      companyName: null,
      deviceId: null,
      deviceName: null,
      attachedEquipmentId: null,
      equipmentName: null
  });

  // 상세 정보 변경 핸들러
  const handleDetailChange = (field, value) => {
    setDetailInfo(prev => ({
        ...prev,
        [field]: value
    }));
  };

  // 수정 모드 전환
  const handleEdit = () => {
    setIsEditMode(true);
  };

  // 취소 핸들러 추가
  const handleCancel = () => {
    if (selectedSensor) {
        handleMenuSelect({ id: selectedSensor.id });
    } else {
        setDetailInfo({
            seq: null,
            companyName: null,
            deviceId: null,
            deviceName: null,
            attachedEquipmentId: null,
            equipmentName: null
        });
    }
    setIsEditMode(false);
  };

  // 정보 클릭 선택 핸들러
  const handleMenuSelect = (params) => {
    const sensor = sensorDeviceList.find(m => m.id === params.id);
      setSelectedSensor(sensor);
      setSelectedEquipment(sensor.attachedEquipmentId)
      setIsEditMode(false);

    if (sensor) {
        setDetailInfo({
            seq: sensor.id || null,
            companyName: sensor.companyName || null,
            deviceId: sensor.deviceId || null,
            deviceName: sensor.deviceName || null,
            attachedEquipmentId: sensor.attachedEquipmentId || null,
            equipmentName: sensor.equipmentName || null,
        });
    }
  };

    // 설비 목록 그리드 컬럼 정의
  const sensorDeviceColumns = [
    { field: 'id', headerName: 'ID', width: 50 },
    { field: 'companyName', headerName: '공장 명', width: 140 },
    { field: 'deviceId', headerName: '센서 ID', width: 140 },
    { field: 'deviceName', headerName: '센서 이름', width: 140 },
    { field: 'attachedEquipmentId', headerName: '설비 ID', width: 140 },
    { field: 'equipmentName', headerName: '설비 이름', width: 350 },
    { field: 'createUser', headerName: '등록자', width: 90 },
    { field: 'createDate', headerName: '등록일', width: 130 },
    { field: 'updateUser', headerName: '수정자', width: 90 },
    { field: 'updateDate', headerName: '수정일', width: 130 }
  ];

  return (
    <Box sx={{p: 0, minHeight: '100vh'}}>
      {/* 헤더 영역 */}
      <PageHeader
        title="설비정보수정"
        setIsHelpModalOpen={setIsHelpModalOpen}
        domain={domain}
        isDarkMode={isDarkMode}
      />

      <Box sx={{ pt: 4, pb: 6 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <Paper elevation={2} sx={{ p: 1, mb: 1 }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12}>
                  <FormControl fullWidth size="large">
                    <InputLabel>지부 선택</InputLabel>
                    <Select
                      value={selectedBranch || ''}
                      label="지부 선택"
                      onChange={(e) => {
                          handleBranchChange(e.target.value);
                          handleCancel();
                          setSelectedSensor(null);
                      }}
                      sx={{ fontSize: '1.1rem', height: '56px' }}
                    >
                        <MenuItem value="" disabled>
                            <em>지부를 선택해주세요 </em>
                        </MenuItem>
                      {branchList.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id} sx={{ fontSize: '1.1rem' }}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Box>

        <Grid container spacing={2}>
            {/*설비정보 목록 출력*/}
            <Grid item xs={12} md={8}>
                <EnhancedDataGridWrapper
                    title="설비 목록"
                    rows={sensorDeviceList}
                    columns={sensorDeviceColumns}
                    onRowClick={handleMenuSelect}
                    height={590}
                />
            </Grid>

            {/*설비정보 상세 정보 영역 */}
            <Grid item xs={12} md={4}>
                <Paper sx={{
                    height: '100%',
                    p: 2,
                    boxShadow: theme.shadows[2],
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                    // bgcolor: getBgColor() // 다른곳에서 불러와야함
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        설비정보 상세 정보
                    </Typography>
                    {selectedSensor != null && !isEditMode && loginUser?.priorityLevel === 5 && (
                        <IconButton
                            color="primary"
                            onClick={handleEdit}
                            size="small"
                        >
                            <Edit />
                        </IconButton>
                    )}
                </Box>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {(selectedSensor || isEditMode) ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <br/>
                                <TextField
                                    fullWidth
                                    label="ID"
                                    value={detailInfo.seq || ''}
                                    disabled="disabled"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="공장명"
                                    value={detailInfo.companyName || ''}
                                    disabled="disabled"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="large">
                                    <InputLabel>설비 선택</InputLabel>
                                    <Select
                                        value={selectedEquipment}
                                        label="설비 선택"
                                        disabled={!isEditMode}
                                        onChange={(e) => {
                                            setSelectedEquipment(e.target.value)
                                            handleDetailChange('attachedEquipmentId', e.target.value)
                                        }}
                                        sx={{ fontSize: '1.1rem', height: '56px' }}
                                    >
                                            <MenuItem value="" disabled>
                                                <em>설비를 선택해주세요 </em>
                                            </MenuItem>
                                        {equipmentList.map((equipment) => (
                                            <MenuItem key={equipment.id} value={equipment.id} sx={{ fontSize: '1.1rem' }}>
                                                {equipment.equipmentName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="설비 ID"
                                    value={detailInfo.attachedEquipmentId || ''}
                                    onChange={(e) => handleDetailChange('attachedEquipmentId', e.target.value)}
                                    disabled="disabled"
                                    required
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="센서 ID"
                                    value={detailInfo.deviceId || ''}
                                    onChange={(e) => handleDetailChange('deviceId', e.target.value)}
                                    disabled={!isEditMode}
                                    required
                                    placeholder="설비 ID를 입력하세요"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="센서 이름"
                                    value={detailInfo.deviceName || ''}
                                    onChange={(e) => handleDetailChange('deviceName', e.target.value)}
                                    disabled={!isEditMode}
                                    required
                                    placeholder="센서 이름을 입력하세요"
                                    size="small"
                                />
                            </Grid>
                            {isEditMode && (
                                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleCancel}
                                        sx={{ mr: 1 }}
                                    >
                                        취소
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={(e) => handleSensorDeviceSave(detailInfo,selectedBranch)}
                                    >
                                        저장
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                        >
                            <Typography variant="body1" color="text.secondary">
                                메뉴를 선택하면 상세 정보가 표시됩니다.
                            </Typography>
                        </Box>
                    )}
                    </Box>
                </Paper>
            </Grid>


        </Grid>

      {/* 도움말 모달 */}
      <HelpContent
        isHelpModalOpen={isHelpModalOpen}
        setIsHelpModalOpen={setIsHelpModalOpen}
        domain={domain}
        isDarkMode={isDarkMode}
      />
    </Box>
  );
};

export default DeviceEquipSync;